import React, { useEffect, useMemo, useState } from "react"
import { Container, Card, CardBody, Table, Button, Input } from "reactstrap"
import { useNavigate, useSearchParams } from "react-router-dom"
import Breadcrumbs from "../../components/Common/Breadcrumb"
import { getExpiryPivot } from "../../services/Hrms/workforce"
import { toastError } from "../../helpers/msg_helper"

const TYPES = [
  { key: "visa", label: "Visa" },
  { key: "labour_card", label: "MOL / Labour Card" },
  { key: "passport", label: "Passport" },
  { key: "emirates_id", label: "Emirates ID" },
  { key: "iloe", label: "ILOE" },
]
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]

const ExpiryReport = () => {
  const navigate = useNavigate()
  const [urlParams] = useSearchParams()
  document.title = "Expiry Report"
  const [type, setType] = useState(urlParams.get("type") || "all")
  const [pivots, setPivots] = useState([]) // array of { type, type_key, years }
  const [loading, setLoading] = useState(false)
  const [year, setYear] = useState(urlParams.get("year") || String(new Date().getFullYear()))

  // distinct years present in the data, for the selector
  const availableYears = useMemo(() => {
    const s = new Set()
    pivots.forEach(p => (p.years || []).forEach(y => s.add(y.year)))
    return Array.from(s).sort((a, b) => a - b)
  }, [pivots])

  // if the chosen year has no data once loaded, fall back to the latest available
  useEffect(() => {
    if (year !== "all" && availableYears.length && !availableYears.includes(Number(year))) {
      setYear(String(availableYears[availableYears.length - 1]))
    }
  }, [availableYears]) // eslint-disable-line react-hooks/exhaustive-deps

  const shownYears = p => (p.years || []).filter(y => year === "all" || String(y.year) === String(year))

  useEffect(() => {
    ;(async () => {
      setLoading(true)
      try {
        const keys = type === "all" ? TYPES.map(t => t.key) : [type]
        const res = await Promise.all(keys.map(k => getExpiryPivot(k).then(r => r?.data).catch(() => null)))
        setPivots(res.filter(p => p && p.years?.length))
      } catch (e) { toastError(e?.message || "Failed to load expiry report") }
      finally { setLoading(false) }
    })()
  }, [type])

  const exportCsv = () => {
    if (!pivots.length) return
    const lines = []
    pivots.forEach(p => {
      shownYears(p).forEach(y => {
        lines.push("")
        lines.push([`${y.year} - ${p.type} Expiry`, ...MONTHS, "Total"].join(","))
        y.companies.forEach(c => lines.push([`"${c.company}"`, ...c.months, c.total].join(",")))
        lines.push(["Grand Total", ...y.grand.months, y.grand.total].join(","))
      })
    })
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" })
    const a = document.createElement("a")
    a.href = URL.createObjectURL(blob)
    a.download = `${type === "all" ? "all-documents" : type}-expiry-report.csv`
    a.click()
    URL.revokeObjectURL(a.href)
  }

  return (
    <div className="page-content report-page">
      <Container fluid>
        <div className="d-flex justify-content-between align-items-center flex-wrap">
          <Breadcrumbs title="HRMS" breadcrumbObject={[
            { title: "HRMS", link: "/hrms" },
            { title: "HR Reports", link: "/hrms-dashboard" },
            { title: "Expiry Report", link: "#" },
          ]} />
          <div className="d-flex align-items-center mb-3" style={{ gap: 8 }}>
            <Input type="select" bsSize="sm" value={type} onChange={e => setType(e.target.value)} style={{ width: 200 }}>
              <option value="all">All documents</option>
              {TYPES.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
            </Input>
            <Input type="select" bsSize="sm" value={year} onChange={e => setYear(e.target.value)} style={{ width: 130 }}>
              <option value="all">All years</option>
              {availableYears.map(y => <option key={y} value={y}>{y}</option>)}
            </Input>
            <Button color="primary" size="sm" onClick={exportCsv} disabled={!pivots.length}>Export CSV</Button>
          </div>
        </div>

        {loading && <Card><CardBody className="text-center text-muted">Loading…</CardBody></Card>}

        {!loading && !pivots.length && (
          <Card><CardBody className="text-center text-muted">No expiry data with expiry dates in scope.</CardBody></Card>
        )}

        {!loading && !!pivots.length && !pivots.some(p => shownYears(p).length) && (
          <Card><CardBody className="text-center text-muted">No expiry data for {year}.</CardBody></Card>
        )}

        {!loading && pivots.map(p => shownYears(p).map(y => (
          <Card className="mb-3" key={`${p.type_key}-${y.year}`}>
            <CardBody>
              <h6 className="fw-semibold mb-3">{y.year} — {p.type} Expiry Details</h6>
              <div style={{ overflowX: "auto" }}>
                <Table bordered className="align-middle tnt-table mb-0" style={{ fontSize: 13 }}>
                  <thead>
                    <tr>
                      <th style={{ minWidth: 220 }}>Company</th>
                      {MONTHS.map(m => <th key={m} className="text-center">{`${m}-${String(y.year).slice(2)}`}</th>)}
                      <th className="text-center">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {y.companies.map((c, i) => (
                      <tr key={i}>
                        <td>{c.company}</td>
                        {c.months.map((n, j) => (
                          <td key={j} className="text-center">
                            {n ? <span role="button" className="text-decoration-underline"
                              onClick={() => navigate(`/hrms-expiry-list?type=${p.type_key}&year=${y.year}&month=${j + 1}`)}>{n}</span> : ""}
                          </td>
                        ))}
                        <td className="text-center fw-600">{c.total}</td>
                      </tr>
                    ))}
                    <tr className="fw-600" style={{ background: "#f1f4f9" }}>
                      <td>Grand Total</td>
                      {y.grand.months.map((n, j) => <td key={j} className="text-center">{n || ""}</td>)}
                      <td className="text-center">{y.grand.total}</td>
                    </tr>
                  </tbody>
                </Table>
              </div>
            </CardBody>
          </Card>
        )))}
      </Container>
    </div>
  )
}

export default ExpiryReport
